import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';

import Loading from 'components/Loading';

import { TemplateMapProps, MapItem, TransportItem, FreightItem } from './types';
import { getCoordinates, getItemCoordinates, formatDate } from './utils';
import { addCircle, addRouteMarkers, fetchAndDrawRoute, setupClusterLayers, setupClusterInteractions } from './mapUtils';
import { getTemplateMapConfig } from './config';
import { useTemplateMapData } from './hooks/useTemplateMapData';
import { MapHeader } from './components/MapHeader';
import { MapSidebar } from './components/MapSidebar';
import { buildClusterPopupHTML } from './components/ClusterPopup';

import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './styles.module.scss';

mapboxgl.accessToken = import.meta.env.VITE_MAP_API_TOKEN;

const MAP_STYLE = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12';

export const TemplateMap: React.FC<TemplateMapProps> = ({ templateId, variant }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = getTemplateMapConfig(variant);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const popup = useRef<mapboxgl.Popup | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [routeDistance, setRouteDistance] = useState<number | null>(null);

  const {
    template,
    items,
    count,
    isLoading,
    error,
    loadingRadius,
    unloadingRadius,
    dateFrom,
    dateTo,
    isRefetching,
    newItemsCount,
    hasMore,
    isLoadingMore,
    setLoadingRadius,
    setUnloadingRadius,
    setDateFrom,
    setDateTo,
    handleApplyFilters,
    handleRefreshNewItems,
    handleLoadMore,
  } = useTemplateMapData({ templateId, variant });

  const sourceName = variant === 'transport' ? 'transports' : 'freights';

  const updateCircles = () => {
    if (!map.current || !template) return;

    const loadingCoords = getCoordinates(template.loading_addresses?.[0]);
    const unloadingCoords = getCoordinates(template.unloading_addresses?.[0]);

    if (loadingCoords && loadingRadius > 0) {
      addCircle(map.current, 'loading-radius', [loadingCoords.lng, loadingCoords.lat], loadingRadius * 1000);
    }
    if (unloadingCoords && unloadingRadius > 0) {
      addCircle(map.current, 'unloading-radius', [unloadingCoords.lng, unloadingCoords.lat], unloadingRadius * 1000);
    }
  };

  const addItemClusters = () => {
    if (!map.current) return;

    const validItems: Array<{ item: MapItem; coords: { lat: number; lng: number } }> = [];
    items.forEach((item) => {
      const coords = getItemCoordinates(item);
      if (coords) validItems.push({ item, coords });
    });

    const geojsonData: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: validItems.map(({ item, coords }) => {
        if (variant === 'transport') {
          const transport = item as TransportItem;
          const loadAddr = transport.loading_address as any;
          const unloadAddr = transport.unloading_address as any;
          const bodytypes = transport.transport_bodytypes?.map(bt => bt.value || bt.title).filter(Boolean).join(', ') || '';
          return {
            type: 'Feature',
            properties: {
              id: transport.id,
              bodytype: bodytypes || transport.transport_bodytype?.value || transport.transport_bodytype?.title || '',
              carrying_capacity: transport.carrying_capacity || 0,
              price: transport.price || 0,
              currency: transport.currency || '',
              address: loadAddr?.full_address || '',
              unloading_address: unloadAddr?.full_address || '',
              from_country: loadAddr?.country_code || '',
              from_zip: loadAddr?.post_code || '',
              from_city: loadAddr?.city || '',
              to_country: unloadAddr?.country_code || '',
              to_zip: unloadAddr?.post_code || '',
              to_city: unloadAddr?.city || '',
              available_from: transport.available_from || '',
              available_to: transport.available_to || '',
              loading_types: transport.loading_types?.join(', ') || '',
            },
            geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] }
          };
        } else {
          const freight = item as FreightItem;
          const loadAddr = freight.loading_address as any;
          const unloadAddr = freight.unloading_address as any;
          return {
            type: 'Feature',
            properties: {
              id: freight.id,
              designation: freight.designation || freight.cargo_designations_additional || '',
              weight: freight.weight || 0,
              volume: freight.volume || 0,
              price: freight.price || 0,
              currency: freight.currency || '',
              bodytype: [freight.bodytype, freight.bodytype_additional].filter(Boolean).join(', '),
              has_adr: freight.adr_captions || (freight.adr_items && freight.adr_items.length > 0) ? '1' : '',
              notes: freight.notes || '',
              from_country: loadAddr?.country_code || '',
              from_zip: loadAddr?.post_code || '',
              from_city: loadAddr?.city || '',
              to_country: unloadAddr?.country_code || '',
              to_zip: unloadAddr?.post_code || '',
              to_city: unloadAddr?.city || '',
              loading_from: freight.loading_from || '',
              loading_to: freight.loading_to || '',
            },
            geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] }
          };
        }
      })
    };

    if (map.current.getSource(sourceName)) {
      (map.current.getSource(sourceName) as mapboxgl.GeoJSONSource).setData(geojsonData);
      return;
    }

    map.current.addSource(sourceName, {
      type: 'geojson',
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 40
    });

    setupClusterLayers(map.current, sourceName);
    setupClusterInteractions(map.current, sourceName);

    let clusterClickTimer: ReturnType<typeof setTimeout> | null = null;

    map.current.on('click', 'clusters', (e) => {
      if (!map.current || !e.features?.[0]) return;

      const feature = e.features[0];
      const clusterId = feature.properties?.cluster_id;
      const pointCount = feature.properties?.point_count;
      const geometry = feature.geometry as GeoJSON.Point;
      const coordinates = geometry.coordinates.slice() as [number, number];

      if (clusterClickTimer) {
        clearTimeout(clusterClickTimer);
        clusterClickTimer = null;
        return;
      }

      clusterClickTimer = setTimeout(() => {
        clusterClickTimer = null;
        if (!map.current) return;

        const source = map.current.getSource(sourceName) as mapboxgl.GeoJSONSource;
        source.getClusterLeaves(clusterId, pointCount, 0, (err, leaves) => {
          if (err || !leaves || !map.current) return;

          popup.current?.remove();

          popup.current = new mapboxgl.Popup({ closeOnClick: true, maxWidth: '300px' })
            .setLngLat(coordinates)
            .setHTML(buildClusterPopupHTML({ leaves, pointCount, variant, config, templateId, styles, t }))
            .addTo(map.current);
        });
      }, 250);
    });

    map.current.on('click', 'unclustered-point', (e) => {
      if (!map.current || !e.features?.[0]) return;

      const feature = e.features[0];
      const props = feature.properties;
      const geometry = feature.geometry as GeoJSON.Point;
      const coordinates = geometry.coordinates.slice() as [number, number];

      setSelectedItemId(props?.id || null);
      popup.current?.remove();

      const fromShort = [props?.from_country, props?.from_zip, props?.from_city].filter(Boolean).join(', ');
      const toShort = [props?.to_country, props?.to_zip, props?.to_city].filter(Boolean).join(', ');

      popup.current = new mapboxgl.Popup({ closeOnClick: true, maxWidth: '450px' })
        .setLngLat(coordinates)
        .setHTML(variant === 'transport'
          ? `
          <div class="${styles.popup}">
            <div class="${styles.popupRouteRow}">
              ${fromShort ? `
                <div class="${styles.popupRoute}">
                  <div class="${styles.popupRouteLabel}">${t('common.from', 'Откуда')}:</div>
                  <div class="${styles.popupRouteAddress}">${fromShort}</div>
                  ${props?.available_from ? `<div class="${styles.popupRouteDate}">${formatDate(props.available_from)}</div>` : ''}
                </div>
              ` : ''}
              ${fromShort && toShort ? `<div class="${styles.popupRouteArrow}">&rarr;</div>` : ''}
              ${toShort ? `
                <div class="${styles.popupRoute}">
                  <div class="${styles.popupRouteLabel}">${t('common.to', 'Куда')}:</div>
                  <div class="${styles.popupRouteAddress}">${toShort}</div>
                  ${props?.available_to ? `<div class="${styles.popupRouteDate}">${formatDate(props.available_to)}</div>` : ''}
                </div>
              ` : ''}
            </div>
            ${props?.bodytype ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('transport.bodyType', 'Тип транспорта')}:</span> ${props.bodytype}</div>` : ''}
            ${props?.loading_types ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('transport.loadingType', 'Тип загрузки')}:</span> ${props.loading_types}</div>` : ''}
            ${props?.carrying_capacity ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('transport.carryingCapacity', 'Грузоподъемность')}:</span> ${props.carrying_capacity} ${t('common.tons', 'т')}</div>` : ''}
            ${props?.price ? `<div class="${styles.popupPrice}">${props.price} ${props.currency || ''}</div>` : `<div class="${styles.popupPrice}">${t('common.priceOnRequest', 'Цена по запросу')}</div>`}
            <button class="${styles.popupButton}" onclick="window.open('${config.routes.search}?templateId=${templateId}&${config.itemIdParam}=${props?.id}', '_blank')">
              ${t('common.open', 'Открыть')}
            </button>
          </div>
          `
          : `
          <div class="${styles.popup}">
            <div class="${styles.popupRouteRow}">
              ${[props?.from_country, props?.from_zip, props?.from_city].filter(Boolean).length ? `
                <div class="${styles.popupRoute}">
                  <div class="${styles.popupRouteLabel}">${t('common.from', 'Откуда')}:</div>
                  <div class="${styles.popupRouteAddress}">${[props?.from_country, props?.from_zip, props?.from_city].filter(Boolean).join(', ')}</div>
                  ${props?.loading_from ? `<div class="${styles.popupRouteDate}">${formatDate(props.loading_from)}</div>` : ''}
                </div>
              ` : ''}
              ${[props?.from_country, props?.from_zip, props?.from_city].filter(Boolean).length && [props?.to_country, props?.to_zip, props?.to_city].filter(Boolean).length ? `<div class="${styles.popupRouteArrow}">&rarr;</div>` : ''}
              ${[props?.to_country, props?.to_zip, props?.to_city].filter(Boolean).length ? `
                <div class="${styles.popupRoute}">
                  <div class="${styles.popupRouteLabel}">${t('common.to', 'Куда')}:</div>
                  <div class="${styles.popupRouteAddress}">${[props?.to_country, props?.to_zip, props?.to_city].filter(Boolean).join(', ')}</div>
                  ${props?.loading_to ? `<div class="${styles.popupRouteDate}">${formatDate(props.loading_to)}</div>` : ''}
                </div>
              ` : ''}
            </div>
            ${props?.designation ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('freight.designation', 'Наименование')}:</span> ${props.designation}</div>` : ''}
            ${props?.bodytype ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('freight.bodyType', 'Тип кузова')}:</span> ${props.bodytype}</div>` : ''}
            ${props?.weight ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('freight.weight', 'Вес')}:</span> ${props.weight} ${t('common.tons', 'т')}</div>` : ''}
            ${props?.volume ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('freight.volume', 'Объём')}:</span> ${props.volume} ${t('common.cubicMeters', 'м³')}</div>` : ''}
            ${props?.has_adr ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">ADR</span></div>` : ''}
            ${props?.price ? `<div class="${styles.popupPrice}"><span class="${styles.popupRowLabel}">${t('freight.price', 'Цена')}:</span> ${props.price} ${props.currency || ''}</div>` : `<div class="${styles.popupPrice}">${t('common.priceOnRequest', 'Цена по запросу')}</div>`}
            ${props?.notes ? `<div class="${styles.popupRow}"><span class="${styles.popupRowLabel}">${t('freight.notes', 'Примечание')}:</span> ${props.notes}</div>` : ''}
            <button class="${styles.popupButton}" onclick="window.open('${config.routes.search}?templateId=${templateId}&${config.itemIdParam}=${props?.id}', '_blank')">
              ${t('common.open', 'Открыть')}
            </button>
          </div>
          `
        )
        .addTo(map.current);
    });
  };

  const fitMapBounds = () => {
    if (!map.current || !template) return;

    const bounds = new mapboxgl.LngLatBounds();
    const loadingCoords = getCoordinates(template.loading_addresses?.[0]);
    const unloadingCoords = getCoordinates(template.unloading_addresses?.[0]);

    if (loadingCoords) bounds.extend([loadingCoords.lng, loadingCoords.lat]);
    if (unloadingCoords) bounds.extend([unloadingCoords.lng, unloadingCoords.lat]);

    items.forEach(item => {
      const coords = getItemCoordinates(item);
      if (coords) bounds.extend([coords.lng, coords.lat]);
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, { padding: { top: 50, bottom: 50, left: sidebarOpen ? 370 : 50, right: 50 } });
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !template || map.current) return;

    const loadingCoords = getCoordinates(template.loading_addresses?.[0]);
    const unloadingCoords = getCoordinates(template.unloading_addresses?.[0]);
    const initLoadingRadius = template.loading_address_radius || 0;
    const initUnloadingRadius = template.unloading_address_radius || 0;

    if (!loadingCoords) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: [loadingCoords.lng, loadingCoords.lat],
      zoom: 6,
      attributionControl: false,
    });

    map.current.addControl(new MapboxLanguage({ defaultLanguage: 'ru' }));
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      if (!map.current) return;
      map.current.resize();

      if (loadingCoords && initLoadingRadius > 0) {
        addCircle(map.current, 'loading-radius', [loadingCoords.lng, loadingCoords.lat], initLoadingRadius * 1000);
      }
      if (unloadingCoords && initUnloadingRadius > 0) {
        addCircle(map.current, 'unloading-radius', [unloadingCoords.lng, unloadingCoords.lat], initUnloadingRadius * 1000);
      }

      if (loadingCoords && unloadingCoords) {
        addRouteMarkers(map.current, loadingCoords, unloadingCoords, styles.routeMarker, markersRef);
        fetchAndDrawRoute(
          map.current,
          [loadingCoords.lng, loadingCoords.lat],
          [unloadingCoords.lng, unloadingCoords.lat],
          mapboxgl.accessToken as string,
          setRouteDistance
        );
      }

      addItemClusters();
      fitMapBounds();
    });

    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, [template, isLoading]);

  // Update clusters when items change
  useEffect(() => {
    if (map.current?.isStyleLoaded() && items.length > 0) {
      addItemClusters();
    }
  }, [items]);

  const handleItemClick = (item: MapItem) => {
    const coords = getItemCoordinates(item);
    if (coords && map.current) {
      setSelectedItemId(item.id);
      map.current.flyTo({ center: [coords.lng, coords.lat], zoom: 14 });
    }
  };

  const handleBack = () => navigate(`${config.routes.search}?templateId=${templateId}`);

  const handleApplyFiltersWithCircles = async () => {
    await handleApplyFilters();
    updateCircles();
  };

  if (isLoading) {
    return <div className={styles.loadingContainer}><Loading /></div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>{error}</p>
        <button onClick={handleBack}>{t(config.translations.back, 'Назад')}</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <MapHeader
        title={template?.template_title}
        routeDistance={routeDistance}
        dateFrom={dateFrom}
        dateTo={dateTo}
        itemsCount={count}
        sidebarOpen={sidebarOpen}
        onBack={handleBack}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        translationKeys={{
          back: config.translations.back,
          templateMap: config.translations.templateMap,
        }}
      />

      {newItemsCount > 0 && (
        <button
          className={styles.refreshButton}
          onClick={handleRefreshNewItems}
          disabled={isRefetching}
        >
          {t(config.translations.refresh, 'Обновить список')}
        </button>
      )}

      <div className={styles.content}>
        <MapSidebar
          isOpen={sidebarOpen}
          variant={variant}
          items={items}
          count={count}
          selectedItemId={selectedItemId}
          loadingRadius={loadingRadius}
          unloadingRadius={unloadingRadius}
          dateFrom={dateFrom}
          dateTo={dateTo}
          isRefetching={isRefetching}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          templateId={templateId}
          searchRoute={config.routes.search}
          itemIdParam={config.itemIdParam}
          translationKeys={{
            loadingRadius: config.translations.loadingRadius,
            unloadingRadius: config.translations.unloadingRadius,
            dateFrom: config.translations.dateFrom,
            dateTo: config.translations.dateTo,
            itemsFound: config.translations.itemsFound,
          }}
          onLoadingRadiusChange={setLoadingRadius}
          onUnloadingRadiusChange={setUnloadingRadius}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onApplyFilters={handleApplyFiltersWithCircles}
          onItemClick={handleItemClick}
          onLoadMore={handleLoadMore}
        />

        <div className={styles.mapContainer}>
          <div ref={mapContainer} className={styles.map} />
        </div>
      </div>
    </div>
  );
};

export default TemplateMap;
